import React, { useState, useCallback } from "react";
import { Box, Typography, Stack, Alert } from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextField } from "@/components/TextField/CTextField";
import { DesignSelector } from "@/features/dmManagement/components/DesignSelector";
import { DesignFileReplace } from "@/features/dmManagement/components/DesignFileReplace";
import { AddressListUpload } from "@/features/dmManagement/components/AddressListUpload";
import { AddressListGrid } from "@/features/dmManagement/components/AddressListGrid";
import { OrderConfirmDialog } from "@/features/dmManagement/components/OrderConfirmDialog";
import { useOrderForm } from "@/features/dmManagement/hooks/useOrderForm";
import type { Design } from "@/features/dmManagement/types";

interface OrderFormProps {
  approvedDesigns: Design[];
}

/**
 * 注文登録フォーム
 * デザイン選択・宛先リストアップロード・ファイル差し替えを含むDM注文の入力フォーム
 */
export const OrderForm: React.FC<OrderFormProps> = ({ approvedDesigns }) => {
  const orderForm = useOrderForm();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [replacements, setReplacements] = useState<Record<number, File>>({});

  const selectedDesignId = orderForm.form.watch("dmDesignId");
  const selectedDesign = approvedDesigns.find((d) => d.id === selectedDesignId);

  const handleDesignChange = useCallback(
    (designId: number): void => {
      setReplacements({});
      orderForm.form.setValue("replacementComponents", undefined);
      orderForm.handleDesignChange(designId);
    },
    [orderForm.form, orderForm.handleDesignChange]
  );

  const handleReplace = useCallback(
    (designComponentId: number, file: File): void => {
      setReplacements((prev) => {
        const next = { ...prev, [designComponentId]: file };
        const replacementComponents = Object.entries(next).map(([id, f]) => ({
          designComponentId: Number(id),
          file: f,
        }));
        orderForm.form.setValue("replacementComponents", replacementComponents);
        return next;
      });
    },
    [orderForm.form]
  );

  const handleRemoveReplace = useCallback(
    (designComponentId: number): void => {
      setReplacements((prev) => {
        const { [designComponentId]: _, ...next } = prev;
        const replacementComponents = Object.entries(next).map(([id, f]) => ({
          designComponentId: Number(id),
          file: f,
        }));
        orderForm.form.setValue(
          "replacementComponents",
          replacementComponents.length > 0 ? replacementComponents : undefined
        );
        return next;
      });
    },
    [orderForm.form]
  );

  const handleConfirmSubmit = useCallback(async (): Promise<void> => {
    try {
      await orderForm.handleSubmit();
    } finally {
      setConfirmOpen(false);
    }
  }, [orderForm.handleSubmit]);

  return (
    <Box sx={{ maxWidth: 800, opacity: orderForm.isSubmitting ? 0.6 : 1 }}>
      <Stack spacing={3}>
        <CTextField
          control={orderForm.form.control}
          name="taskId"
          label="識別番号"
          type="number"
          required
          disabled={orderForm.isSubmitting}
          size={SizingWrapperStyle.INHERIT}
        />

        <CTextField
          control={orderForm.form.control}
          name="taskName"
          label="識別名"
          required
          disabled={orderForm.isSubmitting}
          size={SizingWrapperStyle.INHERIT}
        />

        <DesignSelector
          designs={approvedDesigns}
          selectedDesignId={selectedDesignId}
          onDesignChange={handleDesignChange}
        />

        {selectedDesign && (
          <DesignFileReplace
            components={selectedDesign.components}
            replacements={replacements}
            onReplace={handleReplace}
            onRemoveReplace={handleRemoveReplace}
          />
        )}

        <AddressListUpload
          onFileSelect={orderForm.handleFileSelect}
          onFileRejected={orderForm.handleFileRejected}
          parseStatus={orderForm.parseStatus}
          parseError={orderForm.parseError}
        />

        {orderForm.parseResult && (
          <AddressListGrid
            rows={orderForm.parseResult.rows}
            totalRows={orderForm.parseResult.totalRows}
            hasError={orderForm.parseResult.hasError}
            hasWarning={orderForm.parseResult.hasWarning}
          />
        )}

        {orderForm.parseResult && orderForm.amount != null && (
          <Box>
            <Typography variant="body1">
              発送件数:{" "}
              {orderForm.parseResult.totalRows.toLocaleString("ja-JP")}件
            </Typography>
            {orderForm.unitPrice != null && (
              <Typography variant="body1">
                単価: ¥{orderForm.unitPrice.toLocaleString("ja-JP")}/通
              </Typography>
            )}
            <Typography variant="body1">
              料金: ¥{orderForm.amount.toLocaleString("ja-JP")}（税込）
            </Typography>
          </Box>
        )}

        <Alert severity="warning">注文確定後はキャンセルできません</Alert>

        <Button
          label="注文を確定する"
          variant={ButtonVariantOption.Contained}
          disabled={!orderForm.canSubmit}
          onClick={() => {
            setConfirmOpen(true);
          }}
          size={SizingWrapperStyle.INHERIT}
        />
      </Stack>

      <OrderConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
        }}
        onConfirm={handleConfirmSubmit}
        isSubmitting={orderForm.isSubmitting}
      />
    </Box>
  );
};
